import { useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import cns from 'classnames';
import WidgetCarousel from '../../components/widgets/WidgetCarousel';
import UserCollectionCardLoading from '../../components/widgets/LoadingComponents/UserCollectionCardLoading';
import { IMAGE_PROPS, overlayImageIds } from '../../../shared/utils/ImageUtils';
import ContentCard from '../../components/widgets/ContentCard';
import LoadMorePagination from '../../../shared/components/widgets/LoadMorePagination';
import { DEFAULT_EXPLORE_COLLECTIONS } from '../../../shared/Constants'

export default function ExploreCollectionsDisplayWidget(props) {
    const [collectionPagesToDisplay, setCollectionPagesToDisplay] = useState(1);

    const {
        userCollectionsByCollectionId,
        userCompanyLicensedCollectionsById = {},
        isLoading,
        cardClassName,
        isVerbAdmin = false,
        userCompany = {},
        isReadOnly = false,
        collections,
        shouldSort = true,
        pageLength = 4,
        widgetCarouselClassName = '',
        collectionsById = {},
        loadMoreCollectionsClick,
        totalCollectionsToShow,
        collectionsLoading,
        showBlankSlate,
        noResultsComponent,
        history,
    } = props;

    let renderedCollections = [];
    if (isLoading && !collections.length) {
        renderedCollections = [
            <UserCollectionCardLoading key={1} className={cns('card-loading', cardClassName)} />,
            <UserCollectionCardLoading key={2} className={cns('card-loading', cardClassName)} />
        ];
    }

    const checkForPermalink = (collection) => {
        const updatedCollection = { ...collection };
        if (!updatedCollection.permalink) {
            updatedCollection.permalink = collectionsById[collection._id]?.permalink;
        }
        return updatedCollection;
    };

    if (!isEmpty(collections)) {
        let sortedCollections = [...collections];

        const defaultCollections = [{}, {}, {}, {}];
        if (shouldSort) {
            sortedCollections =
                sortedCollections
                    .reduce((acc, collection) => {
                if (Object.keys(DEFAULT_EXPLORE_COLLECTIONS).includes(collection._id)) {
                    defaultCollections[DEFAULT_EXPLORE_COLLECTIONS[collection._id]] = collection;
                } else {
                    acc.push(collection);
                }
                return acc;
            }, [])
                .sort((a, b) => new Date(b.updatedOn) - new Date(a.updatedOn));
        }

       [...defaultCollections, ...sortedCollections].forEach((collection) => {
            const isUserCollection = userCollectionsByCollectionId[collection._id];
            const isCompanyLicensed = userCompanyLicensedCollectionsById[collection._id];
            const isViewable =
                collection.subscriptionType === 'AvailableForAll' || isUserCollection || isCompanyLicensed;
            if (isUserCollection) {
                const contentCard = (
                    <ContentCard 
                        key={collection._id}
                        imageProps={IMAGE_PROPS.learningAssetImageCloudinary}
                        content={checkForPermalink(collection)}
                        isReadOnly={isReadOnly}
                        className={cardClassName}
                        isVerbAdmin={isVerbAdmin}
                        userCompany={userCompany}
                        cardNumber={renderedCollections.length}
                        fromExplore={true}
                        type={'COLLECTION'}
                        history={history}
                        userSubscription={userCollectionsByCollectionId[collection._id]}
                    />
                )
                renderedCollections.push(contentCard);
            } else if (isViewable) {
                const imageProps = IMAGE_PROPS.learningAssetImageCloudinary;
                let cardOverlayPropsToPass = {};
                if (collection.isLocked) {
                    cardOverlayPropsToPass = {
                        overlayId: overlayImageIds.locked,
                        dimension: {
                            h: 250,
                            w: 250
                        }
                    };
                }
                renderedCollections.push(
                    <ContentCard
                        key={collection._id}
                        imageProps={imageProps}
                        overlayProps={cardOverlayPropsToPass}
                        content={checkForPermalink(collection)}
                        isReadOnly={isReadOnly || collection.isLocked}
                        className={cardClassName}
                        isVerbAdmin={isVerbAdmin}
                        userCompany={userCompany}
                        cardNumber={renderedCollections.length}
                        fromExplore={true}
                        type={'COLLECTION'}
                        history={history}
                    />
                );
            }
        });
    }

    const numCollectionsDisplayed = collectionPagesToDisplay * pageLength;

    const loadMoreClick = () => {
        if (showBlankSlate && numCollectionsDisplayed >= renderedCollections.length) {
            return loadMoreCollectionsClick().then(() => {
                setCollectionPagesToDisplay(collectionPagesToDisplay + 1);
            });
        }
        setCollectionPagesToDisplay(collectionPagesToDisplay + 1);
        return null;
    };

    const totalNum = showBlankSlate ? totalCollectionsToShow : renderedCollections.length;

    let currentlyShownNum;

    if (numCollectionsDisplayed > totalNum) {
        currentlyShownNum = totalNum;
    } else {
        currentlyShownNum = numCollectionsDisplayed;
    }

    if (!showBlankSlate && totalNum < 1) {
        return noResultsComponent;
    }

    return (
        <>
            <WidgetCarousel
                displayItems={renderedCollections.slice(0, pageLength * collectionPagesToDisplay)}
                disableCarousel={true}
                widgetCarouselClassName={widgetCarouselClassName}
            />
            <LoadMorePagination
                name={'explore-collections'}
                totalNum={totalNum}
                currentlyShownNum={currentlyShownNum}
                loadMoreClick={loadMoreClick}
                loading={collectionsLoading}
                showLoadMoreBtn={totalNum > collectionPagesToDisplay * pageLength}
            />
        </>
    );
}

ExploreCollectionsDisplayWidget.propTypes = {
    collections: PropTypes.array,
    isLoading: PropTypes.bool,
    userCollectionsByCollectionId: PropTypes.object,
    disableCarousel: PropTypes.bool,
    cardClassName: PropTypes.string,
    isReadOnly: PropTypes.bool,
    learningAssets: PropTypes.object,
    userLearningAssets: PropTypes.object,
    userCompany: PropTypes.object,
    isVerbAdmin: PropTypes.bool,
    userCompanyLicensedCollectionsById: PropTypes.object,
    sendLibrarySegmentEvents: PropTypes.func,
    shouldSort: PropTypes.bool,
    pageLength: PropTypes.number,
    widgetCarouselClassName: PropTypes.string,
    collectionsById: PropTypes.object,
    loadMoreCollectionsClick: PropTypes.func,
    totalCollectionsToShow: PropTypes.number,
    collectionsLoading: PropTypes.bool,
    showBlankSlate: PropTypes.bool,
    noResultsComponent: PropTypes.object,
    history: PropTypes.object,
};
